"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useDebounceValue = exports.createUseStorage = exports.useMemoOnce = exports.useRun = exports.useRunAsync = exports.useEffectOnce = exports.useNormalizedMemo = exports.useMemoWithPrev = void 0;
const react_1 = require("react");
/**
 * @description useMemoWithPrev is a function that returns a memoized value
 * This function is similar to useMemo, but factory function also receives the previous value.
 * @param factory
 * @param deps
 * @returns
 */
const useMemoWithPrev = (factory, deps) => {
    const prevRef = (0, react_1.useRef)();
    const next = (0, react_1.useMemo)(() => {
        const next = factory(prevRef.current);
        prevRef.current = next;
        return next;
    }, deps !== null && deps !== void 0 ? deps : []);
    return next;
};
exports.useMemoWithPrev = useMemoWithPrev;
/**
 * @description useNormalizedMemo is a function that returns a memoized value
 * This function is similar to useMemoWithPrev, but the return value is normalized.
 * @param factory
 * @param deps
 * @returns
 */
const useNormalizedMemo = (factory, deps) => {
    return (0, exports.useMemoWithPrev)((prev) => {
        const next = factory(prev);
        return normalizeDifference(next, prev)[0];
    }, deps);
};
exports.useNormalizedMemo = useNormalizedMemo;
const normalizeDifference = (newObject, oldObject) => {
    if (newObject && oldObject && typeof newObject === "object") {
        if (!Array.isArray(newObject)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const response = {};
            let changed = false;
            for (const [key, value] of Object.entries(newObject)) {
                const [field, _changed] = normalizeDifference(value, 
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                oldObject[key]);
                response[key] = field;
                changed = changed || _changed;
            }
            for (const key of Object.keys(oldObject)) {
                if (!(key in newObject)) {
                    changed = true;
                }
            }
            return [changed ? response : oldObject, changed];
        }
        else {
            let changed = !Array.isArray(oldObject) || newObject.length !== oldObject.length;
            const response = newObject.map((item, index) => {
                const [field, _changed] = normalizeDifference(item, 
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                oldObject[index]);
                changed = changed || _changed;
                return field;
            });
            return [(changed ? response : oldObject), changed];
        }
    }
    return [newObject, newObject !== oldObject];
};
/**
 * @description This function is similar to useEffect, but it runs only once.
 * This function was created to support the breaking changes of useEffect announced in the React documentation,
 * and to serve as a replacement for useEffect
 * @param effect
 * @param deps
 */
const useEffectOnce = (effect, deps) => {
    let ignore = false;
    const cleanUpFn = (0, react_1.useRef)();
    (0, react_1.useEffect)(() => {
        if (ignore)
            return;
        ignore = true;
        if (cleanUpFn.current) {
            cleanUpFn.current();
        }
        cleanUpFn.current = effect();
        return () => {
            if (cleanUpFn.current) {
                cleanUpFn.current();
                cleanUpFn.current = undefined;
            }
        };
    }, deps);
};
exports.useEffectOnce = useEffectOnce;
/**
 * @description useRunAsync is a function that runs a function only once asynchronously.
 * This function is similar to useRun, but it runs asynchronously.
 * This function was created to support the breaking changes in useEffect that were announced in the React documentation,
 * and to serve as a replacement for useEffect
 * @param fn
 * @param deps
 */
const useRunAsync = (fn, deps) => {
    (0, exports.useRun)(() => {
        setTimeout(fn, 0);
    }, deps);
};
exports.useRunAsync = useRunAsync;
/**
 * @description useRun is a function that runs a function only once synchronously.
 * This function is similar to useMemo, but it does not return a value.
 * This function was created to support the breaking changes in useEffect that were announced in the React documentation,
 * and to serve as a replacement for useEffect
 * @param fn
 * @param deps
 */
const useRun = (fn, deps) => {
    (0, exports.useMemoOnce)(fn, deps);
};
exports.useRun = useRun;
/**
 * @description compareDeps is a function that compares two dependency lists.
 * If the dependency lists are equal, it returns true, otherwise it returns false.
 * @param a
 * @param b
 * @returns true if the dependency lists are equal, false otherwise
 */
const compareDeps = (a, b) => {
    if (a === b)
        return true;
    if (a === undefined || b === undefined)
        return false;
    if (a.length !== b.length)
        return false;
    return a.every((item, index) => item === b[index]);
};
/**
 * @description useMemoOnce is a function that returns a memoized value
 * This function was created to support the breaking changes of useMemo announced in the React documentation,
 * and to serve as a replacement for useMemo
 * @param factory
 * @param deps
 * @returns memoized value
 */
const useMemoOnce = (factory, deps) => {
    const ref = (0, react_1.useRef)();
    if (ref.current === undefined || compareDeps(deps, ref.current.deps)) {
        ref.current = {
            value: factory(),
            deps,
        };
    }
    return ref.current.value;
};
exports.useMemoOnce = useMemoOnce;
const createUseStorage = (key, schema) => {
    return (initialValue, subscribe = false) => {
        const [value, setValue] = (0, react_1.useState)(() => { var _a; return (_a = localStorage.getItem(key)) !== null && _a !== void 0 ? _a : initialValue; });
        (0, react_1.useEffect)(() => {
            if (!subscribe)
                return;
            const changeHandler = (e) => {
                if (e.key === key && e.storageArea === localStorage) {
                    try {
                        const parsed = e.newValue ? schema.parse(e.newValue) : undefined;
                        setValue(parsed);
                    }
                    catch (error) { }
                }
            };
            addEventListener("storage", changeHandler);
            return () => {
                removeEventListener("storage", changeHandler);
            };
        }, [subscribe]);
        const update = (0, react_1.useCallback)((newValue) => {
            setValue(newValue);
            localStorage.setItem(key, newValue);
        }, []);
        return [value, update];
    };
};
exports.createUseStorage = createUseStorage;
function useDebounceValue(value, delay = 500) {
    const [debouncedValue, setDebouncedValue] = (0, react_1.useState)(value);
    (0, react_1.useEffect)(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        // Cancel the timeout if value changes (also on delay change or unmount)
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
}
exports.useDebounceValue = useDebounceValue;
